import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { use } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import DocumentSearchIcon from '@flipster/react/icon/generated/DocumentSearchIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack } from '@flipster/styled-system/patterns';

import { WalletModalTypeContext } from '../core/state';
import { AssetItem } from './asset-item';
import { selectAssetBunja } from './state';

interface SelectCurrencyContext {
  keyword: string;
  currency: string;
  isRecommended: boolean;
  listOrder: number;
  highlights?: string;
}

interface Props {
  onClickCurrency: (currency: string, ctx: SelectCurrencyContext) => void;
  additionalContent?: React.ReactNode;
}
export const SelectAssetContent = ({ onClickCurrency, additionalContent }: Props) => {
  const type = use(WalletModalTypeContext);
  const t = useTranslations();
  const {
    keywordAtom,
    itemsAtom,
    recommendedCurrenciesAtom,
    earnAppliedAprByCurrencyAtom,
    isEmptyAvailableAssetAtom,
    isNoDataAtom,
  } = useBunja(selectAssetBunja);

  const keyword = useAtomValue(keywordAtom);
  const items = useAtomValue(itemsAtom);
  const recommendedCurrencies = useAtomValue(recommendedCurrenciesAtom);
  const earnAppliedAprByCurrency = useAtomValue(earnAppliedAprByCurrencyAtom);
  const isEmptyAvailableAsset = useAtomValue(isEmptyAvailableAssetAtom);
  const isNoData = useAtomValue(isNoDataAtom);

  const onSelectAsset = (ctx: Omit<SelectCurrencyContext, 'keyword'>) => {
    onClickCurrency(ctx.currency, { ...ctx, keyword });
  };
  return (
    <ul className={wrapperStyle}>
      {additionalContent}
      {(isEmptyAvailableAsset || isNoData) && (
        <li className={emptyStyle}>
          <DocumentSearchIcon />
          {isEmptyAvailableAsset && (
            <p>
              <FormattedMessage id='Select_asset.r250401.No_available_assets' />
            </p>
          )}
          {isNoData && (
            <p>
              <FormattedMessage id='Select_asset.r250401.No_data_found' />
            </p>
          )}
        </li>
      )}
      {type === 'deposit' && recommendedCurrencies && (
        <div>
          <p className={descStyle}>
            <FormattedMessage id='Select_asset.r250401.Recommended' />
          </p>
          <ul>
            <div className={hstack({ gap: 6 })}>
              {recommendedCurrencies.map((rc, i) => (
                <li key={`recommended-currency-${rc}`}>
                  <button
                    type='button'
                    onClick={() => onSelectAsset({ currency: rc, listOrder: i, isRecommended: true })}
                    className={recommendItemStyle}
                  >
                    {rc}
                  </button>
                </li>
              ))}
            </div>
          </ul>
        </div>
      )}
      {items.map((item, i) => {
        const earnAppliedApr = type === 'deposit' && earnAppliedAprByCurrency[item.currency];
        const highlightText = earnAppliedApr
          ? t('Autogen_common.r251126.s_earnAppliedApr_toString_APR', {
              earnAppliedApr_toString: earnAppliedApr.toString(),
            })
          : '';
        return (
          <li key={item.currency}>
            <button
              className={css({ width: '100%' })}
              type='button'
              onClick={() => {
                onSelectAsset({
                  currency: item.currency,
                  listOrder: i,
                  isRecommended: false,
                  highlights: highlightText,
                });
              }}
            >
              <AssetItem item={item} earnAppliedAprByCurrency={earnAppliedAprByCurrency} />
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const wrapperStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
});

const emptyStyle = flex({
  justify: 'center',
  align: 'center',
  direction: 'column',
  gap: 8,
  textStyle: 'Legacy/Body3',
  p: { base: 64, tablet: 104 },
  '& svg': {
    color: 'gray.500',
  },
  '& > p': {
    color: 'gray.700',
  },
});

const descStyle = css({
  textStyle: 'Legacy/Body2',
  mb: '8px',
  color: 'gray.500',
});

const recommendItemStyle = css({
  textStyle: 'Legacy/Body2',
  p: '8px 12px',
  color: 'gray.white',
  border: '1px solid token(colors.gray.300)',
  borderRadius: '1000px',
});
